import { useState, useCallback, useEffect } from 'react';
import { getOrderImages, OrderImageRecord } from '../services/orderImagesService';
import { uploadOrderImages, deleteOrderImage } from '../services/lineItemImagesService';
import { ShippingLineItem } from '../DeShippingTableWrapper';

interface UseLineItemImagesReturn {
  imageViewerVisible: boolean;
  selectedLineItemImages: OrderImageRecord[];
  isLoading: boolean;
  isUploading: boolean;
  deletingImages: Set<number>;
  error: string | null;
  setImageViewerVisible: (visible: boolean) => void;
  refetch: () => void;
  uploadImages: (files: File[], workflowId: number | undefined, orderId: number | undefined, lineItemId: number | undefined) => Promise<any>;
  deleteImage: (imageId: number) => Promise<void>;
  onHide: () => void;
  openImageViewer: (workflowId: number) => void;
}

export const useLineItemImages = (selectedLineItem: ShippingLineItem | null): UseLineItemImagesReturn => {
  const [imageViewerVisible, setImageViewerVisible] = useState(false);
  const [selectedLineItemImages, setSelectedLineItemImages] = useState<OrderImageRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingImages, setDeletingImages] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!selectedLineItem) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const images = await getOrderImages(selectedLineItem.workflow_id);
      setSelectedLineItemImages(images);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch images');
    } finally {
      setIsLoading(false);
    }
  }, [selectedLineItem]);

  useEffect(() => {
    if (selectedLineItem) {
      refetch();
    }
  }, [selectedLineItem]);

  const openImageViewer = useCallback((workflowId: number) => {
    setImageViewerVisible(true);
    refetch();
  }, [refetch]);

  const onHide = useCallback(() => {
    setImageViewerVisible(false);
    setSelectedLineItemImages([]);
    setError(null);
    setIsLoading(false);
    setDeletingImages(new Set());
  }, []);

  const uploadImages = useCallback(async (files: File[], workflowId: number | undefined,
                                      orderId: number | undefined, lineItemId: number | undefined) => {
    if (!workflowId || !orderId || !lineItemId) return;

    setIsUploading(true);
    setError(null);

    try {
      const result = await uploadOrderImages(workflowId, orderId.toString(), lineItemId.toString(), files);
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload images');
      throw err;
    } finally {
      setIsUploading(false);
    }
  }, []);

  const deleteImage = useCallback(async (imageId: number) => {
    // Add to deleting set
    setDeletingImages(prev => {
      const newSet = new Set(prev);
      newSet.add(imageId);
      return newSet;
    });
    setError(null);

    try {
      await deleteOrderImage(imageId);
      
      // Remove from images array
      setSelectedLineItemImages(prev => 
        prev.filter(image => image.id !== imageId)
      );
      
      // Remove from deleting set
      setDeletingImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(imageId);
        return newSet;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete image');
      // Remove from deleting set on error
      setDeletingImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(imageId);
        return newSet;
      });
    }
  }, []);

  return {
    // State
    imageViewerVisible,
    selectedLineItemImages,
    isLoading,
    isUploading,
    deletingImages,
    error,
    openImageViewer,
    // Actions
    setImageViewerVisible,
    uploadImages,
    deleteImage,
    refetch,
    onHide,
  };
};